import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { voiceQueries } from '../queries';
import { useVoiceSessionStore } from '../voice-session-store';
import { NamespaceBoundary } from '@/lib/i18n/NamespaceBoundary';
import { VoiceLobby } from './VoiceLobby';

const loadVoiceRoom = () => import('./VoiceRoom');
const VoiceRoom = React.lazy(() =>
  loadVoiceRoom().then((module) => ({ default: module.VoiceRoom })),
);

/**
 * App-wide voice session host. Mounted ONCE in providers (outside the route
 * tree) so the LiveKit connection survives navigation — the agent joins a voice
 * channel and keeps talking while browsing Inbox/CRM/etc.
 *
 * Phase gate: `lobby` → pre-join device check (`VoiceLobby`); `live` → the
 * connected room (`VoiceRoom`, expanded overlay or minimized pill). Absent when
 * no session is active. Keyed on channelId so switching channels rebuilds clean.
 */
export function VoiceDock() {
  const revision = useVoiceSessionStore(s=>s.revision);
  const channelId = useVoiceSessionStore((s) => s.channelId);
  const channelName = useVoiceSessionStore((s) => s.channelName);
  const phase = useVoiceSessionStore((s) => s.phase);
  const minimized = useVoiceSessionStore((s) => s.minimized);
  const devicePrefs = useVoiceSessionStore((s) => s.devicePrefs);
  const enter = useVoiceSessionStore((s) => s.enter);
  const setMinimized = useVoiceSessionStore((s) => s.setMinimized);
  const leave = useVoiceSessionStore((s) => s.leave);

  useQuery(voiceQueries.participants(channelId ?? ''));

  // The lobby gives us a natural prefetch window. LiveKit stays out of the
  // initial app bundle, but is normally ready before the user presses Join.
  React.useEffect(() => {
    if (channelId) void loadVoiceRoom();
  }, [channelId]);

  if (!channelId) return null;

  if (phase === 'lobby') {
    return (
      <NamespaceBoundary namespace="voice"><VoiceLobby
        key={channelId+':'+revision}
        channelName={channelName}
        onEnter={enter}
        onCancel={leave}
      /></NamespaceBoundary>
    );
  }

  return (
    <NamespaceBoundary namespace="voice"><React.Suspense fallback={null}>
      <VoiceRoom
        key={channelId+':'+revision}
        channelId={channelId}
        channelName={channelName}
        devicePrefs={devicePrefs}
        minimized={minimized}
        onMinimize={() => setMinimized(true)}
        onExpand={() => setMinimized(false)}
        onClose={leave}
      />
    </React.Suspense></NamespaceBoundary>
  );
}
